import React from "react";
import { Link } from "react-router-dom";
import apiClient from "../../../api/client";
import AuthService from "../../../api/authService";
import {
  updateMyCommunityProfile,
  uploadMyProfileBackground,
} from "../../../api/communityService";
import PseudoField from "../../../components/forms/PseudoField";
import { useAuth } from "../../../context/AuthContext";
import ProfileSectionCard from "../components/ProfileSectionCard";
import { useProfileHub } from "../ProfileHubContext";
import ProfileAvatarsSection from "./ProfileAvatarsSection";

const CURRENT_YEAR = new Date().getFullYear();

function computeAge(birthYear) {
  if (!birthYear) return null;
  const age = CURRENT_YEAR - Number(birthYear);
  return age > 0 && age < 120 ? age : null;
}

export default function ProfileAccountSection() {
  const { updateUser, logout } = useAuth();
  const { profileIdentity, refreshProfile } = useProfileHub();

  const [pseudo, setPseudo] = React.useState(profileIdentity.pseudo || "");
  const [bio, setBio] = React.useState(profileIdentity.bio || "");
  const [isPublic, setIsPublic] = React.useState(profileIdentity.is_public !== false);
  const [city, setCity] = React.useState(profileIdentity.city || "");
  const [birthYear, setBirthYear] = React.useState(profileIdentity.birth_year || "");

  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [backgroundFile, setBackgroundFile] = React.useState(null);
  const [isUploadingBackground, setIsUploadingBackground] = React.useState(false);
  const [backgroundMessage, setBackgroundMessage] = React.useState("");
  const [backgroundError, setBackgroundError] = React.useState("");

  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [passwordMessage, setPasswordMessage] = React.useState("");
  const [isChangingPassword, setIsChangingPassword] = React.useState(false);

  const resetPasswordModal = () => {
    setShowPasswordModal(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordMessage("");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordMessage("");
    setIsChangingPassword(true);
    try {
      const response = await AuthService.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setPasswordMessage(response.message || "Mot de passe modifie avec succes.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPasswordError(
        err?.response?.data?.error ||
          (err instanceof Error ? err.message : "Impossible de modifier le mot de passe.")
      );
    } finally {
      setIsChangingPassword(false);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = React.useState(false);
  const [deletePseudoInput, setDeletePseudoInput] = React.useState("");
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState("");

  React.useEffect(() => {
    setPseudo(profileIdentity.pseudo || "");
    setBio(profileIdentity.bio || "");
    setIsPublic(profileIdentity.is_public !== false);
    setCity(profileIdentity.city || "");
    setBirthYear(profileIdentity.birth_year || "");
  }, [
    profileIdentity.bio,
    profileIdentity.birth_year,
    profileIdentity.city,
    profileIdentity.is_public,
    profileIdentity.pseudo,
  ]);

  const handleSave = async () => {
    setIsSaving(true);
    setMessage("");
    setError("");
    try {
      const response = await updateMyCommunityProfile({
        pseudo,
        bio,
        is_public: isPublic,
        city,
        birth_year: birthYear ? Number(birthYear) : null,
      });
      setMessage(response.message);
      updateUser?.({ username: pseudo });
      await refreshProfile?.();
    } catch (saveError) {
      const serverError = saveError?.response?.data;
      const suggestedPseudo = serverError?.suggested_pseudo || "";
      setError(
        serverError?.error ||
          (saveError instanceof Error ? saveError.message : "Impossible de mettre le profil a jour.")
      );
      if (suggestedPseudo) setPseudo(suggestedPseudo);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setDeleteError("");
    try {
      await apiClient.delete("me/delete-account/");
      logout?.();
    } catch {
      setDeleteError("Erreur lors de la suppression. Contacte le support.");
      setIsDeleting(false);
    }
  };

  const handleUploadBackground = async () => {
    if (!backgroundFile) {
      setBackgroundError("Choisis une image de bannière.");
      return;
    }
    setIsUploadingBackground(true);
    setBackgroundMessage("");
    setBackgroundError("");
    try {
      const response = await uploadMyProfileBackground(backgroundFile);
      setBackgroundMessage(response.message || "Bannière mise à jour.");
      setBackgroundFile(null);
      await refreshProfile?.();
    } catch (uploadError) {
      setBackgroundError(
        uploadError?.response?.data?.error ||
          (uploadError instanceof Error ? uploadError.message : "Impossible d'envoyer la bannière.")
      );
    } finally {
      setIsUploadingBackground(false);
    }
  };

  const age = computeAge(profileIdentity.birth_year);

  return (
    <div className="space-y-6">
      {/* Infos du compte */}
      <ProfileSectionCard
        title="Mon compte"
        subtitle="Informations du joueur visibles sur ton profil public."
      >
        <div className="space-y-4">
          {error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}
          {message && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {message}
            </div>
          )}

          <PseudoField
            value={pseudo}
            onChange={setPseudo}
            label="Pseudo"
            placeholder="Votre pseudo"
          />

          <label className="block">
            <div className="mb-1.5 text-sm font-semibold text-slate-900">Email</div>
            <input
              type="email"
              value={profileIdentity.email || ""}
              disabled
              className="w-full cursor-not-allowed rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500 outline-none"
            />
          </label>

          <label className="block">
            <div className="mb-1.5 text-sm font-semibold text-slate-900">Ville / Région</div>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              maxLength={120}
              placeholder="Paris, Lyon, Bruxelles..."
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-pixy-blue"
            />
          </label>

          <label className="block">
            <div className="mb-1.5 text-sm font-semibold text-slate-900">
              Année de naissance
              {age !== null && (
                <span className="ml-2 font-normal text-slate-500">({age} ans)</span>
              )}
            </div>
            <input
              type="number"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
              min={1920}
              max={CURRENT_YEAR - 5}
              placeholder="Ex : 1995"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-pixy-blue"
            />
          </label>

          <label className="block">
            <div className="mb-1.5 text-sm font-semibold text-slate-900">Bio</div>
            <textarea
              rows={4}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="w-full resize-none rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-pixy-blue"
            />
          </label>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="rounded-full bg-pixy-ink px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSaving ? "Enregistrement..." : "Enregistrer"}
            </button>
            {profileIdentity.public_slug && (
              <Link
                to={`/joueur/u/${profileIdentity.id}`}
                className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Voir mon profil public
              </Link>
            )}
            <button
              type="button"
              onClick={() => setShowPasswordModal(true)}
              className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Changer le mot de passe
            </button>
          </div>
        </div>
      </ProfileSectionCard>

      <ProfileSectionCard
        title="Bannière de profil"
        subtitle="Image affichée dans le header de ton profil privé et public."
      >
        <div className="space-y-4">
          <div
            className="h-32 overflow-hidden rounded-2xl border border-slate-200 bg-cover bg-center bg-slate-50"
            style={{
              backgroundImage: profileIdentity.profile_background
                ? `url(${profileIdentity.profile_background})`
                : "linear-gradient(135deg,#effcf6,#e9f7ff)",
            }}
          />
          {backgroundError && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {backgroundError}
            </div>
          )}
          {backgroundMessage && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {backgroundMessage}
            </div>
          )}
          <label className="block">
            <div className="mb-1.5 text-sm font-semibold text-slate-900">
              Uploader une bannière
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                setBackgroundFile(event.target.files?.[0] || null);
                setBackgroundMessage("");
                setBackgroundError("");
              }}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600"
            />
          </label>
          <button
            type="button"
            onClick={handleUploadBackground}
            disabled={isUploadingBackground || !backgroundFile}
            className="rounded-full bg-pixy-ink px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isUploadingBackground ? "Envoi..." : "Mettre à jour ma bannière"}
          </button>
        </div>
      </ProfileSectionCard>

      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">Changer le mot de passe</h3>

            {passwordError && (
              <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {passwordError}
              </div>
            )}
            {passwordMessage && (
              <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {passwordMessage}
              </div>
            )}

            <form onSubmit={handleChangePassword} className="mt-4 space-y-3">
              <label className="block">
                <div className="mb-1.5 text-sm font-semibold text-slate-900">Mot de passe actuel</div>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-pixy-blue"
                />
              </label>
              <label className="block">
                <div className="mb-1.5 text-sm font-semibold text-slate-900">Nouveau mot de passe</div>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-pixy-blue"
                />
              </label>
              <label className="block">
                <div className="mb-1.5 text-sm font-semibold text-slate-900">Confirmer le mot de passe</div>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-pixy-blue"
                />
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="rounded-full bg-pixy-ink px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isChangingPassword ? "Enregistrement..." : "Valider"}
                </button>
                <button
                  type="button"
                  onClick={resetPasswordModal}
                  className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Fermer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Supprimer le compte */}
      <ProfileSectionCard
        title="Supprimer mon compte"
        subtitle="Cette action est irréversible. Ton compte sera désactivé définitivement."
      >
        {!deleteConfirm ? (
          <button
            type="button"
            onClick={() => { setDeleteConfirm(true); setDeletePseudoInput(""); setDeleteError(""); }}
            className="rounded-full border border-rose-200 px-5 py-3 text-sm font-semibold text-rose-700 transition hover:bg-rose-50"
          >
            Supprimer mon compte
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              Pour confirmer, tape ton pseudo <strong>{profileIdentity.pseudo}</strong> ci-dessous.
            </p>
            <input
              type="text"
              value={deletePseudoInput}
              onChange={(e) => setDeletePseudoInput(e.target.value)}
              placeholder={profileIdentity.pseudo}
              className="w-full rounded-2xl border border-rose-200 px-4 py-3 text-sm outline-none focus:border-rose-500"
            />
            {deleteError && (
              <p className="text-sm text-rose-600">{deleteError}</p>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={isDeleting || deletePseudoInput !== profileIdentity.pseudo}
                className="rounded-full bg-rose-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDeleting ? "Suppression..." : "Supprimer définitivement"}
              </button>
              <button
                type="button"
                onClick={() => setDeleteConfirm(false)}
                className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </ProfileSectionCard>

      <ProfileAvatarsSection />
    </div>
  );
}
